// interpolateErrorMessage.ts
//
// A code's copy is one flat string, but some errors arrive carrying data — the message
// lockout sends `lockedUntil` beside its code. `{fieldName}` names a field on the API
// error item, so the value lives with the response and the copy stays in the catalogue.

import { formatDateTime } from '../../lib/helpers/dates';

const PLACEHOLDER = /\{(\w+)\}/g;
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T/;

/**
 * Fills `{field}` placeholders from the API error item, formatting ISO/ATOM values with
 * formatDateTime. A line whose placeholder cannot be resolved is dropped rather than
 * shown half-written — Error.vue already renders each line as its own paragraph.
 */
export function interpolateErrorMessage(message: string, values: Record<string, unknown>): string {
  if (!message.includes('{')) return message;

  return message
    .split('\n')
    .map((line) => resolveLine(line, values))
    .filter((line): line is string => line !== null)
    .join('\n');
}

function resolveLine(line: string, values: Record<string, unknown>): string | null {
  let unresolved = false;

  const filled = line.replace(PLACEHOLDER, (_match, field: string) => {
    const value = values[field];

    if (typeof value !== 'string' || !value.trim()) {
      unresolved = true;
      return '';
    }

    if (!ISO_DATE_TIME.test(value)) return value;

    const formatted = formatDateTime(value);
    if (!formatted) unresolved = true;

    return formatted;
  });

  return unresolved ? null : filled;
}
